
import {defineComponent} from "vue";
import {Campaign, CollectUserData} from "@/models/campaign";
import {CampaignFetcher} from "@/views/survey/ts/campaignFetcher";
import campaignBrandingManager from "@/views/survey/ts/campaignBrandingManager";
import SurveyAgentView, {OnSurveyAgentViewFinished, totalDuration} from "@/views/survey/components/SurveyAgentView.vue";
import FooterComponent from "@/components/recorder/partials/FooterComponent.vue";
import SurveyDetailsView, {OnDetailsCollectedPayload} from "@/views/survey/components/SurveyDetailsView.vue";
import SurveyLoaderView from "@/views/survey/components/SurveyLoaderView.vue";
import {SurveyUploader} from "@/views/survey/ts/surveyUploader";
import SurveyFinishView from "@/views/survey/components/SurveyFinishView.vue";
import BackgroundImageMixin from "@/components/recorder/BackgroundImageMixin.vue";
import {mapActions} from "pinia";
import {AnalyticsAction, useAnalyticsStore} from "@/store/analytics.store";
import SurveyVideoView from "@/views/survey/components/SurveyVideoView.vue";
import SurveyHeaderComponent from "./components/SurveyHeaderComponent.vue";
import SurveyNPSView from "@/views/survey/components/SurveyNPSView.vue";
import SurveyCSATView from "@/views/survey/components/SurveyCSATView.vue";
import {Helper} from "@/utils/helper";
import SurveyInactiveView from "@/views/survey/components/SurveyInactiveView.vue";

// TODO: Remove when recorder is moved to new design - remove import
import LegacyRecorderView, { RecorderState } from "@/views/recorder/LegacyRecorderView.vue";

interface Data {
    error: string | undefined,
    campaign: Campaign;
    surveyFinishedPayload?: OnSurveyAgentViewFinished;
    collectedDetails?: OnDetailsCollectedPayload;
    loaderMessage: string;
    state: SurveyState;
    isPreview: boolean;
    rating?: number;
    fetchError: boolean
    isTextSurvey: boolean;
    canHideLoader: boolean;
    // TODO: Remove when recorder is moved to new design
    oldPreviewState: RecorderState | undefined;
}

export enum SurveyState {
    Inactive = 'inactive',
    Initial = 'initial',
    Details = 'details',
    Loader = 'loader',
    Finished = 'finished',
    Video = 'video',
    NPS = 'nps',
    CSAT = 'csat',
}

export default defineComponent({
    components: {
        SurveyInactiveView,
        LegacyRecorderView,
        SurveyCSATView,
        SurveyVideoView,
        SurveyAgentView,
        SurveyDetailsView,
        SurveyLoaderView,
        FooterComponent,
        SurveyFinishView,
        SurveyHeaderComponent,
        SurveyNPSView,
    },
    data(): Data {
        return {
            error: undefined,
            campaign: undefined as unknown as Campaign,
            surveyFinishedPayload: undefined,
            collectedDetails: undefined,
            loaderMessage: '',
            state: SurveyState.Initial,
            isPreview: false,
            rating: undefined,
            fetchError: false,
            isTextSurvey: false,
            canHideLoader: false,


            // TODO: Remove when recorder is moved to new design
            oldPreviewState: undefined,
        };
    },
    mixins: [BackgroundImageMixin],
    async mounted() {
        this.isPreview = !!this.$route.query.isPreview
        const href = this.extractHref();
        if (!this.isPreview && href === undefined) {
            return this.$router.push({ name: 'home' });
        }

        const fetcher = new CampaignFetcher(this.isPreview, href);
        // TODO: Remove when recorder is moved to new design - remove RecorderState from callback
        fetcher.listen((campaign?: Campaign, state?: SurveyState | RecorderState, error?: boolean) => {
            if (!campaign || error) {
                this.fetchError = true;
                return;
            }

            this.campaign = campaign;

            // TODO: Remove when recorder is moved to new design
            if (!this.campaign.newRecorder) {
                this.oldPreviewState = state ? state as RecorderState : undefined;
                return;
            }

            if (this.showInactiveView) {
                this.state = SurveyState.Inactive;
            } else if (state) {
                if (state === SurveyState.Details && !this.collectFurtherDetails) {
                    this.state = SurveyState.Finished;
                } else {
                    this.state = state as SurveyState;
                }
            } else if (this.campaign.videoURLs) {
                this.state = SurveyState.Video;
            } else if (this.campaign.isCSATCampaign) {
                this.state = SurveyState.CSAT;
            } else if (this.campaign.isNPSCampaign) {
                this.state = SurveyState.NPS;
            }

            this.$i18n.locale = campaign.interfaceLanguage.split("-")[0].toLowerCase()
            this.loaderMessage = this.$t('survey.loaderMessage');
            campaignBrandingManager.setDocumentColorsFor(campaign)

            if (!this.isPreview) {
                this.trackPageView(this.$route.query);
                this.trackEvent("creationInitiated", {
                    ...this.$analytics.campaignParams(this.campaign),
                });

                this.sendAnalyticsEvent(AnalyticsAction.SurveyLinkOpened, this.campaign.id);
            }
        })
    },
    computed: {
        inAppPresentation(): boolean {
          return typeof window.BeamsSDK !== 'undefined';
        },
        showInactiveView(): boolean {
            return (this.campaign.status !== 'active' || this.campaign.canAcceptResponses === false) && !this.isPreview;
        },
        isInitialState(): boolean {
            return this.state === SurveyState.Initial;
        },
        isDetailsState(): boolean {
            return this.state === SurveyState.Details;
        },
        isLoadingState(): boolean {
            return this.state === SurveyState.Loader;
        },
        isVideoState(): boolean {
            return this.state === SurveyState.Video;
        },
        isFinishedState(): boolean {
            return this.state === SurveyState.Finished;
        },
        isNPSState(): boolean {
            return this.state === SurveyState.NPS;
        },
        isCSATState(): boolean {
            return this.state === SurveyState.CSAT;
        },
        isInactiveState(): boolean {
            return this.state === SurveyState.Inactive;
        },

        NPSQuestion(): string {
            return `${this.campaign.agentWelcomeMessage || this.$t('survey.defaultAgentWelcomeMessage')} ${this.campaign.NPSQuestion}`;
        },

        CSATQuestion(): string {
            return `${this.campaign.agentWelcomeMessage || this.$t('survey.defaultAgentWelcomeMessage')} ${this.campaign.CSATQuestion}`;
        },

        promptQuestion() {
            if (this.campaign.isNPSCampaign || this.campaign.isCSATCampaign) {
                return this.campaign.promptText;
            } else {
                return `${this.campaign.agentWelcomeMessage || this.$t('survey.defaultAgentWelcomeMessage')} ${this.campaign.promptText}`;
            }
        },
        combinedSurveyQuestion(): string {
            if (this.campaign.isNPSCampaign) {
                return `${this.NPSQuestion} ${this.campaign.promptText}`;
            } else if (this.campaign.isCSATCampaign) {
                return `${this.CSATQuestion} ${this.campaign.promptText}`;
            } else {
                return this.promptQuestion;
            }
        },
        collectFurtherDetails() {
            return this.campaign.collectEmails != CollectUserData.Disabled || this.campaign.collectNames != CollectUserData.Disabled || this.campaign.enforcesTermsAndConditionsConsent || this.campaign.enforcesPrivacyPolicyConsent;
        },
    },
    methods: {
        // analytics store
        ...mapActions(useAnalyticsStore, ['trackPageView']),
        ...mapActions(useAnalyticsStore, ['trackEvent']),
        ...mapActions(useAnalyticsStore, ['sendAnalyticsEvent']),

        sdkClose() {
            window.BeamsSDK?.close();
        },
        async onSurveyFinished(surveyFinishedPayload: OnSurveyAgentViewFinished) {
            this.surveyFinishedPayload = surveyFinishedPayload;
            this.isTextSurvey = surveyFinishedPayload.isTextSurvey;

            if (!this.collectFurtherDetails) {
                await this.doUpload();
            } else {
                this.state = SurveyState.Details;
                this.trackEvent('creationDetailsOpened', {
                    ...this.$analytics.campaignParams(this.campaign),
                    duration: totalDuration(surveyFinishedPayload.conversationalSurveyRounds || []),
                });
            }
        },
        async onSurveyDetailsFinished(data: OnDetailsCollectedPayload) {
            this.collectedDetails = data;
            await this.doUpload();
        },
        async doUpload() {
            if (this.isPreview) {
                return;
            }

            if (!this.surveyFinishedPayload) {
                throw new Error("unexpected state, survey text or audio data is undefined");
            }

            this.state = SurveyState.Loader;
            setTimeout(() => {
                this.canHideLoader = true;
                this.onUploadFinished();
            }, 3000);
            this.trackEvent('creationSubmitted', {
                ...this.$analytics.campaignParams(this.campaign),
                duration: totalDuration(this.surveyFinishedPayload.conversationalSurveyRounds || []),
            });

            const uploader = new SurveyUploader();

            try {
                await uploader.upload({
                    isTextSurvey: this.isTextSurvey,
                    campaignID: this.campaign.id,
                    email: this.collectedDetails?.email,
                    name: this.collectedDetails?.name,
                    rating: this.rating,
                    externalID: this.$route.query.externalID as string | undefined,
                    surveyFinishedPayload: this.surveyFinishedPayload
                });
                this.trackEvent('creationCompleted', {
                    ...this.$analytics.campaignParams(this.campaign),
                    duration: totalDuration(this.surveyFinishedPayload.conversationalSurveyRounds || []),
                });

                this.onUploadFinished()
                this.sendMessageToEmbeddingParent();
            } catch(e) {
                this.error = e as string;
                console.error("Upload failed!", e as string);
                this.onUploadFinished();
                this.trackEvent('creationErrored', {
                    ...this.$analytics.campaignParams(this.campaign),
                    duration: totalDuration(this.surveyFinishedPayload.conversationalSurveyRounds || []),
                    error: e as string,
                });
            }
        },
        sendMessageToEmbeddingParent(): void {
            if (!window.Cypress) {
                Helper.sendMessageToParentWindow({ type: 'Beams::CreationCompleted' });
            }
        },
        onUploadFinished() {
            if (this.error) {
                this.loaderMessage = this.$t('survey.uploadError');
            } else if (this.canHideLoader) {
                this.state = SurveyState.Finished;
            }
        },
        tryAgainClicked() {
            this.loaderMessage = this.$t('survey.loaderMessage');
            this.error = undefined;
            this.doUpload();
        },
        onVideoFinished() {
            if (this.campaign.isNPSCampaign) {
                this.state = SurveyState.NPS;
            } else if (this.campaign.isCSATCampaign) {
                this.state = SurveyState.CSAT;
            } else {
                this.state = SurveyState.Initial;
            }

            this.sendAnalyticsEvent(AnalyticsAction.VideoPrerollCompleted, this.campaign.id);
        },
        onNPSRatingSubmitted(rating: number): void {
            this.rating = rating;
            this.state = SurveyState.Initial;
            this.sendAnalyticsEvent(AnalyticsAction.NPSScoreSubmitted, this.campaign.id);
        },
        onCSATRatingSubmitted(rating: number): void {
            this.rating = rating;
            this.state = SurveyState.Initial;
            this.sendAnalyticsEvent(AnalyticsAction.CSATScoreSubmitted, this.campaign.id);
        },
        extractHref(): string | undefined {
            const href = this.$route.params.href;
            if (typeof href === 'string') {
                return href;
            } else if (Array.isArray(href)) {
                return href[0];
            } else {
                return undefined;
            }
        },
    },
});
